import httpClient from '../../../api/httpClient';
import API_CONFIG from '../../../config/api.config';
import { 
  CreditDebitTracker, 
  CreditDebitTrackerFilterParams,
  CreditDebitTrackerFormData,
  AccountType,
  AccountBalance
} from '../types/credit-debit-tracker.types';

export const creditDebitTrackerApi = {
  /**
   * Get paginated list of credit/debit transactions
   */
  getCreditDebitTrackers: async (params: CreditDebitTrackerFilterParams): Promise<CreditDebitTracker[]> => {
    const response = await httpClient.get<CreditDebitTracker[]>(API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER, { params });
    return response.data;
  },

  /**
   * Get a single transaction by ID
   */
  getCreditDebitTrackerById: async (id: string): Promise<CreditDebitTracker> => {
    const response = await httpClient.get<CreditDebitTracker>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/${id}`);
    return response.data;
  },

  /**
   * Get transactions by type
   */
  getCreditDebitTrackersByType: async (type: AccountType): Promise<CreditDebitTracker[]> => {
    const response = await httpClient.get<CreditDebitTracker[]>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/type/${type}`);
    return response.data;
  },

  /**
   * Get transactions by chart of account
   */
  getCreditDebitTrackersByAccount: async (chartOfAccountId: string): Promise<CreditDebitTracker[]> => {
    const response = await httpClient.get<CreditDebitTracker[]>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/account/${chartOfAccountId}`);
    return response.data;
  },

  /**
   * Get transactions by location
   */
  getCreditDebitTrackersByLocation: async (locationId: string): Promise<CreditDebitTracker[]> => {
    const response = await httpClient.get<CreditDebitTracker[]>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/location/${locationId}`);
    return response.data;
  },

  /**
   * Get transactions by date range
   */
  getCreditDebitTrackersByDateRange: async (
    startDate: string,
    endDate: string,
    filters?: {
      type?: AccountType;
      chartOfAccountId?: string;
      locationId?: string;
    }
  ): Promise<CreditDebitTracker[]> => {
    const params = {
      startDate,
      endDate,
      ...filters,
    };
    const response = await httpClient.get<CreditDebitTracker[]>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/reports/date-range`, { params });
    return response.data;
  },

  /**
   * Get account balance
   */
  getAccountBalance: async (chartOfAccountId: string, endDate?: string): Promise<AccountBalance> => {
    const params = endDate ? { endDate } : {};
    const response = await httpClient.get<AccountBalance>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/account/${chartOfAccountId}/balance`, { params });
    return response.data;
  },

  /**
   * Create a new transaction
   */
  createCreditDebitTracker: async (data: CreditDebitTrackerFormData): Promise<CreditDebitTracker> => {
    const response = await httpClient.post<CreditDebitTracker>(API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER, data);
    return response.data;
  },

  /**
   * Update an existing transaction
   */
  updateCreditDebitTracker: async (id: string, data: Partial<CreditDebitTrackerFormData>): Promise<CreditDebitTracker> => {
    const response = await httpClient.patch<CreditDebitTracker>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/${id}`, data);
    return response.data;
  },

  /**
   * Delete a transaction (soft delete)
   */
  deleteCreditDebitTracker: async (id: string): Promise<CreditDebitTracker> => {
    const response = await httpClient.delete<CreditDebitTracker>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/${id}`);
    return response.data;
  },

  /**
   * Update transaction status
   */
  updateCreditDebitTrackerStatus: async (id: string, isActive: boolean): Promise<CreditDebitTracker> => {
    const response = await httpClient.patch<CreditDebitTracker>(`${API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER}/${id}/status`, null, {
      params: { isActive }
    });
    return response.data;
  },

  /**
   * Search transactions
   */
  searchCreditDebitTrackers: async (searchTerm: string): Promise<CreditDebitTracker[]> => {
    const response = await httpClient.get<CreditDebitTracker[]>(API_CONFIG.ENDPOINTS.CREDIT_DEBIT_TRACKER, {
      params: { search: searchTerm }
    });
    return response.data;
  }
};
